import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../services/auth';
import { telegramService } from '../services/telegram';
import { getStoredTranslationOrDefault } from '../utils/bibleLocaleSync';
import { t } from '../i18n/uiText';

function Login({
  onLogin,
  showToast,
  asModal = false,
  initialNextPath = '',
  onDone,
  onCancel,
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authFlowStarted = useRef(false);
  const [loading, setLoading] = useState(true);
  const [isInTelegram, setIsInTelegram] = useState(false);
  const [email, setEmail] = useState(() => authService.getStoredLoginEmail());
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  useEffect(() => {
    if (authFlowStarted.current) return;
    const authTokenFromLink = searchParams.get('auth') || searchParams.get('magic');
    const authToken = searchParams.get('token');
    const taskId = searchParams.get('taskId');

    setIsInTelegram(telegramService.isTelegramWebApp);

    if (authTokenFromLink) {
      authFlowStarted.current = true;
      void handleMagicVerify(authTokenFromLink, taskId);
    } else if (authToken) {
      authFlowStarted.current = true;
      void handleTokenExchange(authToken, taskId);
    } else {
      setEmail((prev) => prev.trim() || authService.getStoredLoginEmail());
      setLoading(false);
    }
  }, []);

  const goAfterLogin = (taskId) => {
    const fromProp =
      typeof initialNextPath === 'string' &&
      initialNextPath.startsWith('/') &&
      !initialNextPath.startsWith('//')
        ? initialNextPath
        : null;
    const fromQuery = searchParams.get('next');
    const safeFromQuery =
      fromQuery && fromQuery.startsWith('/') && !fromQuery.startsWith('//')
        ? fromQuery
        : null;
    const nextPath = fromProp || safeFromQuery || `/${getStoredTranslationOrDefault()}`;

    if (typeof onDone === 'function') {
      onDone({ taskId, nextPath });
      return;
    }

    if (taskId) {
      window.location.href = `/create?taskId=${taskId}`;
      return;
    }
    navigate(nextPath, { replace: true });
  };

  const handleMagicVerify = async (magicToken, taskId) => {
    try {
      await authService.verifyMagicLink(magicToken);
      const token = authService.getAuthToken();
      onLogin(token);
      showToast(`✅ ${t('loginSuccessByLink')}`, 'success');
      goAfterLogin(taskId);
    } catch (error) {
      console.error('Magic-link verify error:', error);
      showToast(`❌ ${t('loginLinkInvalidOrExpired')}`, 'error');
      setLoading(false);
    }
  };

  const handleTokenExchange = async (oneTimeToken, taskId) => {
    try {
      const response = await authService.exchangeToken(oneTimeToken);
      onLogin(response.accessToken || response.token);
      showToast(`✅ ${t('loginSuccess')}`, 'success');
      goAfterLogin(taskId);
    } catch (error) {
      console.error('Token exchange error:', error);
      showToast(`❌ ${t('loginError')}`, 'error');
      setLoading(false);
    }
  };

  const handleTestLogin = async () => {
    try {
      setLoading(true);
      const response = await authService.getTestToken();
      onLogin(response.accessToken || response.token);
      showToast('✅ Тестовый вход', 'success');
      goAfterLogin(null);
    } catch (error) {
      console.error('Login error:', error);
      showToast('❌ Ошибка: ' + error.message, 'error');
      setLoading(false);
    }
  };

  const handleRequestMagicLink = async (e) => {
    e.preventDefault();
    const normalizedEmail = String(email || '').trim().toLowerCase();
    if (!normalizedEmail) {
      showToast(t('loginEnterEmail'), 'error');
      return;
    }
    try {
      setEmailSubmitting(true);
      await authService.requestMagicLink(normalizedEmail, window.location.origin);
      setLinkSent(true);
    } catch (error) {
      console.error('Magic-link request error:', error);
      showToast(`❌ ${t('loginSendLinkFailed')}`, 'error');
    } finally {
      setEmailSubmitting(false);
    }
  };

  const handleCloseLogin = () => {
    if (typeof onCancel === 'function') {
      onCancel();
      return;
    }
    navigate(`/${getStoredTranslationOrDefault()}`, { replace: true });
  };

  if (loading) {
    return (
      <div className={`bf-login-shell ${asModal ? 'bf-login-shell--modal' : ''}`}>
        <div className="text-center w-full max-w-md">
          <div className="bf-login-card__spinner" aria-hidden="true" />
          <p className="bf-login-card__loading-text">{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bf-login-shell ${asModal ? 'bf-login-shell--modal' : ''}`}>
      <div className="bf-login-card space-y-6">
        {asModal && (
          <button
            type="button"
            onClick={() => onCancel?.()}
            className="bf-login-card__close"
            aria-label={t('close')}
          >
            ×
          </button>
        )}
        <div className="text-center">
          <p className="bf-login-card__title">{t('loginByEmailForFeatures')}</p>
        </div>

        {isInTelegram ? (
          <div className="bf-login-card__telegram space-y-4">
            <svg className="bf-login-card__telegram-icon" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
            <p className="bf-login-card__telegram-title">{t('loginOpenedInTelegram')}</p>
            <p className="bf-login-card__telegram-sub">{t('loginTelegramAutoAuth')}</p>
          </div>
        ) : (
          <>
            {linkSent ? (
              <div className="space-y-3">
                <div className="bf-login-card__notice">{t('loginCheckEmailLinkSent')}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <button type="button" onClick={() => setLinkSent(false)} className="bf-login-card__btn-primary">
                    {t('loginSendAgain')}
                  </button>
                  <button type="button" onClick={handleCloseLogin} className="bf-login-card__btn-ghost">
                    {t('close')}
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleRequestMagicLink} className="space-y-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="bf-login-card__input"
                  autoComplete="email"
                />
                <button type="submit" disabled={emailSubmitting} className="bf-login-card__btn-primary">
                  <span>{emailSubmitting ? t('loginSending') : t('loginByEmailButton')}</span>
                </button>
              </form>
            )}
          </>
        )}

        <p className="bf-login-card__hint">
          {isInTelegram ? t('loginTelegramRefreshHint') : t('loginEmailReturnHint')}
        </p>
      </div>
    </div>
  );
}

export default Login;
